import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { CinchDialog } from '@cinch/ui';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import Input from '@mui/material/Input';
import InputLabel from '@mui/material/InputLabel';
import { QRCodeSVG } from 'qrcode.react';
import { snackbarService } from 'components/mui/snackbar/snackbar-service';
import { setupTotp, confirmTotp } from 'shared/auth.api';

// Forced enrollment shown when MFA is required but the user has no phone and no authenticator.
// Mirrors NeedsPasswordResetModal: driven by the backend `requires_totp_setup` flag and dismissed
// by clearing it once the authenticator is confirmed.
export default function NeedsTotpSetupModal(props) {
  const {
    open,
    me,
    setUser,
    logOut
  } = props;
  const [setup, setSetup] = useState(null); // { otpauth_uri, secret }
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Generate a secret as soon as the modal opens so the QR is ready to scan.
  useEffect(() => {
    if (open && !setup && !isLoading) {
      setIsLoading(true);
      setupTotp().then(({
        data
      }) => setSetup(data)).catch(() => snackbarService.popup({
        type: 'error',
        message: 'Unable to start authenticator setup. Please try again.'
      })).then(() => setIsLoading(false));
    }
  }, [open, setup, isLoading]);
  const submitCode = () => {
    if (!code || code.length !== 6) {
      snackbarService.popup({
        type: 'error',
        message: 'Please enter the 6-digit code from your authenticator app.'
      });
      return;
    }
    setIsLoading(true);
    confirmTotp(code).then(() => {
      setUser({
        ...me,
        requires_totp_setup: false,
        totp_active: true,
        require_mfa: true
      });
    }).catch(() => snackbarService.popup({
      type: 'error',
      message: 'That code did not match. Please try again.'
    })).then(() => setIsLoading(false));
  };
  return <CinchDialog open={open} onClose={() => {}} hideClose customWidth="400px" customHeight="auto" title={<Stack alignItems="center" component="span">
          <ShieldOutlinedIcon sx={{
      fontSize: 40,
      color: '#12aae1'
    }} />
          <Typography variant="h4" component="span">
            Set up multi-factor authentication
          </Typography>
        </Stack>} bodyData={<>
          <Box component="p">
            A company you have access to requires multi-factor authentication. Set up an authenticator
            app (such as Google Authenticator or Authy) to continue.
          </Box>
          <Box component="p">
            If you would like to log in as a different account you'll need to{' '}
            <Button component="a" onClick={logOut} variant="text">
              log out
            </Button>
          </Box>

          {setup && <Box>
              <Box component="p">1. Scan this QR code with your authenticator app:</Box>
              <Stack alignItems="center" sx={{
        my: 2
      }}>
                <QRCodeSVG value={setup.otpauth_uri} size={180} />
              </Stack>
              <Box component="p">
                Or enter this key manually: <code>{setup.secret}</code>
              </Box>
              <InputLabel disabled={isLoading} sx={{
        marginTop: '15px'
      }}>
                2. Enter the 6-digit code from your app
              </InputLabel>
              <Input id="iTotpSetupCode" disabled={isLoading} fullWidth={true} required={true} value={code} onChange={e => setCode(e.target.value)} placeholder="123456" />
            </Box>}
        </>} actions={<div style={{
    display: 'flex',
    justifyContent: 'flex-end'
  }}>
          <Button onClick={submitCode} disabled={isLoading || !setup} variant="text">
            Verify and enable
          </Button>
        </div>} />;
}